'use client'

import { useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field } from '@/components/encuentro/ui/field'
import { isPlausibleLoginCode, LOGIN_CODE_MAX_LENGTH } from '@/lib/auth/login-code'
import type { RequestLoginCodeAction, VerifyLoginCodeAction } from '@/lib/auth/login-code'
import { useLoginCode, type LoginCodeError } from '@/lib/auth/use-login-code'

import { AuthHeading, authLinkClass } from './auth-shell'

/** 44 px touch target for a text link (sistema: controls 44 px). */
const LINK_TARGET = 'inline-flex min-h-en-control items-center'

const ERROR_KEY: Record<LoginCodeError, string> = {
  invalid_email: 'errors.invalidEmail',
  invalid_code: 'errors.invalidCode',
  rate_limited: 'errors.rateLimited',
  failed: 'errors.failed',
  profile_error: 'errors.profileError',
}

/**
 * "Entrar con un código por mail" of the Encuentro login (session audit R4):
 * address, then the code. Same state machine as the classic screen
 * (lib/auth/use-login-code.ts); results are the same for every address.
 */
export function LoginCodePanel({
  request,
  verify,
  next,
  initialEmail,
  focusEmail = false,
  hasInstitution,
  onBack,
}: {
  request: RequestLoginCodeAction
  verify: VerifyLoginCodeAction
  next?: string | null
  initialEmail?: string
  /** The page named a school: a flag for the sign-in measurement, never which one. */
  hasInstitution?: boolean
  /** Focus the address field on mount (empty: just focus; prefilled: select it too). */
  focusEmail?: boolean
  onBack: () => void
}) {
  const t = useTranslations('encuentro.auth.login.code')
  const flow = useLoginCode({ request, verify, next, initialEmail, hasInstitution })
  // A wrong code must not say "ask for a new one" while the resend is still
  // cooling down: say how long is left instead.
  const errorKey =
    flow.error === 'invalid_code' && flow.cooldown > 0
      ? 'errors.invalidCodeWait'
      : flow.error
        ? ERROR_KEY[flow.error]
        : null
  const errorText = errorKey ? t(errorKey as never, { seconds: flow.cooldown } as never) : undefined

  // After a failure the focus goes back to the field, with its content
  // selected, so the next attempt is typing over it.
  const emailRef = useRef<HTMLInputElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const errorSeen = useRef<string | null>(null)
  useEffect(() => {
    if (!focusEmail) return
    const field = emailRef.current
    field?.focus()
    if (field?.value) field.select()
  }, [focusEmail])
  // Another code on request: the field is where the person goes next.
  const resend = async () => {
    if (await flow.send()) codeRef.current?.focus()
  }
  useEffect(() => {
    if (!flow.error || flow.busy) {
      if (!flow.error) errorSeen.current = null
      return
    }
    if (errorSeen.current === flow.error) return
    errorSeen.current = flow.error
    const target = flow.step === 'code' ? codeRef.current : emailRef.current
    target?.focus()
    if (flow.step === 'code') target?.select()
  }, [flow.error, flow.busy, flow.step])

  return (
    <div data-testid="encuentro-login-code" className="grid gap-5">
      <AuthHeading title={t('title')} subtitle={t('subtitle')} />

      {flow.step === 'email' ? (
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!flow.busy) void flow.send()
          }}
        >
          <Field
            label={t('emailLabel')}
            id="login-code-email"
            name="email"
            type="email"
            value={flow.email}
            onChange={(e) => flow.setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            enterKeyHint="send"
            required
            ref={emailRef}
            readOnly={flow.busy}
            aria-busy={flow.busy || undefined}
            error={errorText}
          />
          <Button
            look="encuentro"
            variant="primary"
            type="submit"
            block
            disabled={flow.cooldown > 0 || !flow.email}
            aria-disabled={flow.busy || undefined}
          >
            {flow.busy && <Loader2 aria-hidden className="animate-spin" />}
            {flow.busy ? t('sending') : t('send')}
          </Button>
        </form>
      ) : (
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!flow.busy) void flow.submitCode()
          }}
        >
          <p role="status" className="text-en-ink-muted">
            {t('sent', { email: flow.email })}
          </p>
          <Field
            label={t('codeLabel')}
            help={t('codeHelp')}
            id="login-code"
            name="code"
            type="text"
            value={flow.code}
            onChange={(e) => flow.setCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={LOGIN_CODE_MAX_LENGTH}
            enterKeyHint="done"
            autoFocus
            required
            ref={codeRef}
            readOnly={flow.busy}
            aria-busy={flow.busy || undefined}
            inputClassName="text-center text-en-heading tabular-nums tracking-widest"
            error={errorText}
          />
          <Button
            look="encuentro"
            variant="primary"
            type="submit"
            block
            disabled={!isPlausibleLoginCode(flow.code)}
            aria-disabled={flow.busy || undefined}
          >
            {flow.busy && <Loader2 aria-hidden className="animate-spin" />}
            {flow.busy ? t('verifying') : t('verify')}
          </Button>
          <div className="grid justify-items-center text-en-meta">
            <button
              type="button"
              onClick={() => void resend()}
              disabled={flow.busy || flow.cooldown > 0}
              className={`${LINK_TARGET} font-semibold ${authLinkClass} disabled:text-en-ink-muted disabled:no-underline`}
            >
              {flow.cooldown > 0 ? t('resendIn', { seconds: flow.cooldown }) : t('resend')}
            </button>
            <button
              type="button"
              onClick={flow.changeEmail}
              className={`${LINK_TARGET} ${authLinkClass}`}
            >
              {t('changeEmail')}
            </button>
          </div>
        </form>
      )}

      <p className="text-center text-en-meta">
        <button type="button" onClick={onBack} className={`${LINK_TARGET} ${authLinkClass}`}>
          {t('back')}
        </button>
      </p>
    </div>
  )
}
